import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { Component, inject, Input, OnInit } from "@angular/core";

import { Router } from "@angular/router";

import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";

import { FiatService } from "@main-services/currency/fiat.service";
import { SavingsPlan } from "@models/savings_plan";
import { TradableTradeSummaryComponent } from "./tradable-trade-summary.component";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { config } from "@config";
import { UserService } from "@main-services/user.service";
import { empty, truncate } from "@helpers/utils";
import { SavingsModel } from "@models/savings";
import { AssetInfoService } from "@main-services/split-data/asset-info.service";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { TranslationService } from "@core-services/translation.service";
import { InfoService } from "@core-services/info.service";
import { TradeOfferService } from "../../services/trade-offer.service";
import { ITBTrade } from "@modules/shared/components/instant-trade-bonus/entitites/itb-trade";
import { LeveragedTokensFacade } from "@modules/main/components/leveraged-tokens/state/leveraged-tokens.facade";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { CRYPTO_TYPE, TRADABLES_TYPE } from "@helpers/constants";
import { SecurityTokenToggleSlider } from "@models/security-token-toggles";
import { AppropriatenessFacade } from "@modules/main/components/stocks/appropriateness-modal/+state/appropriateness.facade";
import { CheckoutDataService } from "@modules/checkout/services/checkout-data.facade";
import { catchError, combineLatest, map, Observable, of, take } from "rxjs";
import {
	OrderTypeTrackingProperty,
	TradeTrackingName,
	TradeTypeTrackingProperty
} from "@modules/checkout/entities/trade.rudderstack.tracking.model";
import { SegmentService } from "@main-services/segment.service";
import {
	tradablesWithoutAppropriateness,
	UserJourney
} from "@modules/shared/terms-and-conditions/entities/terms-and-conditions.model";
import { Tradable } from "@models/interfaces/tradable";
import { visionBenefitToggleTexts, VSNOptinToggleViewModel } from "@modules/web3/interface/web3.benefit.ui.interface";
import { Web3RemoteService } from "@modules/web3/services/web3.remote";
import { IVSNFeeTokenCollection } from "@models";
import { Web3Facade } from "@modules/web3/+state/web3.facade";
import { VsnSavingPlanState } from "./vsn-summary.state";
import { RudderstackService } from "@main-services/rudderstack.service";

@UntilDestroy()
@Component({
	selector: "savings-summary",
	templateUrl: "./savings-summary-trade.component.html",
	styleUrls: ["./savings-summary-trade.component.scss"],
	providers: [CheckoutDataService],
	standalone: false
})
export class SavingsSummaryTradeComponent extends TradableTradeSummaryComponent implements OnInit {
	private web3RemoteService = inject(Web3RemoteService);
	private webFacade = inject(Web3Facade);
	private vsnSavingPlanState = inject(VsnSavingPlanState);

	@Input() public savingsData: SavingsModel = null;

	public confirmCC: boolean = false;
	public isSecurityToken: boolean = false;
	public securityTokenToggles: SecurityTokenToggleSlider = new SecurityTokenToggleSlider();
	public dataService = inject(CheckoutDataService);
	public segmentService = inject(SegmentService);
	public rudderstackService = inject(RudderstackService);
	public tradableInfo$;
	public isHodl: boolean = false;
	public isUserNotApp: boolean = false;
	public userNonAppTerms: boolean = false;
	public tradable: Tradable = null;

	feeTransparencyEnabledFF$ = this.launchDarklyService.isActive$(LD_FLAG.FEE_TRANSPARENCY_CRYPTO_ONLY);

	private savingsMigrateToFiatIdEnabled = false;
	private savingsMigrateToFiatIdEnabled$ = this.launchDarklyService.isActive$(LD_FLAG.MIGRATE_SAVINGS_TO_FIAT_ID);

	//fee benefit VSNOptinToggleViewModel
	vsnSavingFeeBenefit$: Observable<VSNOptinToggleViewModel | null> = combineLatest({
		settings: this.web3RemoteService.getUserSettings(),
		vsnPid: this.webFacade.getVisionPid()
	}).pipe(
		catchError(() => {
			return of(null);
		}),
		map(({ settings, vsnPid }) => {
			const texts = visionBenefitToggleTexts;
			let vsnSavingCollection = this.savingsData.planExample.getVsnFeeTokenCollection();
			if (!vsnSavingCollection || Object.keys(vsnSavingCollection).length === 0) {
				return null;
			}
			const { eligibleForVsn, fee_discount } = vsnSavingCollection;
			const { visionBonus } = settings;

			const globalOptIn = settings === null ? null : visionBonus;

			//default
			const visionFeeDetail = {
				visionRate: "0",
				visionAmount: "0",
				feeBefore: "0",
				hasEnoughFunds: true,
				bonus: null
			};

			const vsnFeeCollection: IVSNFeeTokenCollection = {
				visionFeeDetail,
				eligibleForVsn,
				globalOptIn,
				enabled: true,
				feeDiscount: fee_discount.toString()
			};

			//set state for later use in success screen
			this.vsnSavingPlanState.setVsnSavingPlan({
				feeDiscount: fee_discount.toString(),
				globalOptIn,
				visionBonus,
				eligibleForVsn,
				isAssetVSN:
					this.tradableListService.getItemById(this.savingsData.tradeData.getCurrencyId())?.getPid() ===
					vsnPid
			});

			texts.heading.discount = fee_discount;

			const model: VSNOptinToggleViewModel = {
				texts,
				vsnFeeCollection
			};

			return model;
		})
	);

	vsnAsset$ = this.webFacade.getVisionTradable$();

	public constructor(
		router: Router,
		protected apicallService: ApicallService,
		protected fiatwalletService: FiatWalletService,
		protected walletListService: TradableWalletListService,
		protected fiatService: FiatService,
		protected tradableListService: TradableListService,
		protected translationService: TranslationService,
		protected infoService: InfoService,
		protected masterdataService: MasterdataService,
		protected tradeOfferService: TradeOfferService,
		protected userService: UserService,
		protected assetInfoService: AssetInfoService,
		protected leveragedTokensFacade: LeveragedTokensFacade,
		protected launchDarklyService: LaunchDarklyService,
		protected appropriatenessFacade: AppropriatenessFacade
	) {
		super(
			router,
			apicallService,
			fiatwalletService,
			walletListService,
			fiatService,
			tradableListService,
			translationService,
			infoService,
			masterdataService,
			tradeOfferService,
			userService,
			assetInfoService,
			leveragedTokensFacade,
			launchDarklyService
		);
	}

	public ngOnInit(): void {
		this.savingsMigrateToFiatIdEnabled$.pipe(take(1)).subscribe((flag) => {
			this.savingsMigrateToFiatIdEnabled = flag;
		});
		const tradable = this.tradableListService.getItemById(this.savingsData.tradeData.getCurrencyId());
		this.tradable = tradable;
		this.tradableInfo$ = this.dataService.getTradeableInfo$(tradable.getId());
		this.isSecurityToken = tradable.getCurrencyType() === TRADABLES_TYPE.SECURITY_TOKEN;
		this.isHodl = !(tradable.isWithdrawActive() && tradable.isDepositActive());
		this.appropriatenessFacade
			.isStockNotAppropriate$()
			.pipe(untilDestroyed(this))
			.subscribe((isNotAppropriate) => {
				this.isUserNotApp = isNotAppropriate;
			});

		//set up agreement bundle data
		const userJourneys = [this.savingsData.planExample.getUserJourney()];
		const tradableType = tradable.getCurrencyType();
		if (tradablesWithoutAppropriateness.indexOf(tradableType) === -1 && this.userService.user.needsRiskToggle()) {
			userJourneys.push(UserJourney.AppropriatenessWarning);
		}
		//CHECK: notice what happens if userJourneys returns empty array
		this.termsAndConditionsService
			.getAgreementBundleData$(userJourneys)
			.pipe(take(1))
			.subscribe((bundles) => {
				this.bundles = bundles;
			});
	}

	// Override
	public acceptOffer(): void {
		if (this.dynamicTermsInvalid) {
			this.dynamic_terms?.wiggleIt();
			return;
		}
		if (this.bundles.length > 0) {
			const tradable = this.tradableListService.getItemById(this.savingsData.tradeData.getCurrencyId());
			const userJourneys = [this.savingsData.planExample.getUserJourney()];
			const tradableType = tradable.getCurrencyType();
			if (
				tradablesWithoutAppropriateness.indexOf(tradableType) === -1 &&
				this.userService.user.needsRiskToggle()
			) {
				userJourneys.push(UserJourney.AppropriatenessWarning);
			}
			super.acceptTermsAndBuy(userJourneys);
		} else {
			this.confirmPlanToAPI();
		}
	}

	// Override
	public refresh(): void {
		this.data["amount"] =
			this.data.getSelectedCurrency() === "cryptocoin"
				? this.data.getAmountTradable()
				: this.data.getAmountFiat();
	}

	public isHodlAsset(): boolean {
		return this.asset.isHodlTradable("buy");
	}

	public isCrypto(): boolean {
		return this.asset.getCurrencyType() === CRYPTO_TYPE;
	}

	public getIsMandatePlan(): boolean {
		if (this.savingsData) {
			return this.savingsData.tradeData.getMandateUsed();
		}
		return false;
	}

	public getInitialBuyDate(): string {
		if (this.getIsMandatePlan()) {
			return this.savingsData.getInitialBuyFormatted(true);
		}
		return this.translationService.translate("recurring_buy_modal_plan_details_initial_buy_now");
	}

	// Override
	public get acceptedAll(): boolean {
		return this.confirmCC && super.acceptedAll;
	}

	public getProviderLogo(): string {
		return this.masterdataService.logoUrl(this.savingsData.paymentOption);
	}

	public getProviderFee(): string {
		const fee = this.savingsData.paymentOption.getFeeByFiatId(this.fiat.getId());
		return this.round(fee.getFeePercent()) + "%";
	}

	private round(fix: number): string {
		return "" + truncate(fix, this.fiat.getPrecision());
	}

	protected confirmPlanToAPI(): void {
		const model = this.savingsData;

		const wallet = this.walletListService.getItemById(model.tradeData.getWalletId());

		const tradable = wallet.isDefault()
			? { cryptocoin_id: model.tradeData.getCurrencyId() }
			: { wallet_id: model.tradeData.getWalletId() };
		let confirmData = {};
		confirmData = {
			...tradable,
			fiat_id: model.tradeData.getFiatId(),
			fiat_wallet_id: model.tradeData.getFiatWalletId(),
			payment_option_id: model.paymentOption.getId(),
			fiat_amount: model.tradeData.getAmountFiat(),
			frequency: model.frequencySelector.primary,
			day: empty(model.frequencySelector.secondary) ? 0 : model.frequencySelector.secondary, // User selects daily hour only
			time: model.frequencySelector.time,
			time_zone: model.getTimeZone(),
			bfc_used: model.tradeData.bestFeeOptIn,
			mandate_used: model.tradeData.getMandateUsed()
		};
		if (this.savingsMigrateToFiatIdEnabled) {
			confirmData = {
				...tradable,
				fiat_id: model.tradeData.getFiatId(),
				payment_option_id: model.paymentOption.getId(),
				fiat_amount: model.tradeData.getAmountFiat(),
				frequency: model.frequencySelector.primary,
				day: empty(model.frequencySelector.secondary) ? 0 : model.frequencySelector.secondary, // User selects daily hour only
				time: model.frequencySelector.time,
				time_zone: model.getTimeZone(),
				bfc_used: model.tradeData.bestFeeOptIn,
				mandate_used: model.tradeData.getMandateUsed()
			};
		}
		const options = new ApicallOption(SavingsPlan, confirmData);
		this.finish.emit(options);
	}

	// Override
	public getSavingsLink(): string {
		return (config.baseUrl + config.externalPaths["tradeOffer"]["singleAccount"]).replace(
			"%s",
			TranslationService.getLanguageIsoCode()
		);
	}

	public getItbData(): ITBTrade {
		return {
			amount: "" + +this.savingsData?.tradeData?.getAmountFiat() * +this.fiat?.toEuroRate(),
			itbRate: this.savingsData?.planExample?.getITBRate()
		};
	}

	public secTokenNonAppVal(event: boolean): void {
		this.userNonAppTerms = event;
	}

	public get isButtonDisabled(): boolean {
		return this.dynamicTermsInvalid;
	}

	public getButtonState(): boolean {
		if (!this.isSecurityToken) {
			return !this.acceptedAll;
		}
		if (this.isUserNotApp) {
			return !this.acceptedAll || !this.hodlOnly || !this.userNonAppTerms;
		}
		return !this.acceptedAll || !this.hodlOnly;
	}

	trackFeeTransparency(): void {
		const properties = {
			trade_type: TradeTypeTrackingProperty.saving_plans,
			order_type: OrderTypeTrackingProperty.market_order
		};
		const name = TradeTrackingName.TradeSummaryFeeSectionTapped;
		const data = { name, properties };

		this.segmentService.track(data);
		this.rudderstackService.track(data);
	}
}
